import { useState } from 'react';
import { Download, FileUp, Loader2 } from 'lucide-react';
import { useCampaignStore } from '@/stores';
import { authService } from '@/services/authService';
import { API_BASE_URL } from '@/config';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { triggerBrowserDownload } from '@/components/finance/reports/downloadHelpers';

/**
 * Demo-only "Import files" control (ADR-056, beat 2). Gated purely on the selected campaign's
 * `isDemo` flag; the backend endpoints 403 any non-demo campaign as defense-in-depth.
 *
 * There is no one-click server-side simulate. Instead the rep downloads two clean sample CSVs — a
 * contributions sheet and an expenses sheet, modeled on real campaign exports — and uploads them
 * through the real "Upload files" flow, mapping the columns exactly as a customer would. That's the
 * honest import experience: the expenses sheet auto-maps, the contributions sheet exercises the
 * mapping grid, and the itemized individual donors surface the missing occupation/employer nag on
 * import (while the organization row correctly does not).
 */
export function DemoImportCard() {
  const selectedCampaign = useCampaignStore((s) => s.selectedCampaign);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<'contributions' | 'expenses' | null>(null);

  const download = async (kind: 'contributions' | 'expenses', fileName: string, endpoint: string) => {
    setDownloading(kind);
    try {
      const response = await authService.authenticatedFetch(`${API_BASE_URL}/api/finance/dev/${endpoint}`);
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      triggerBrowserDownload(blob, fileName);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Download failed');
    } finally {
      setDownloading(null);
    }
  };

  if (!selectedCampaign?.isDemo) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <FileUp className="h-4 w-4" />
          Import files
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Download these two sample sheets, then bring them in through <strong>Upload files</strong> just
          like a real campaign export. The expenses sheet maps itself; the contributions sheet walks you
          through the column-mapping grid, and its itemized donors surface the missing
          occupation/employer flags for North Carolina.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => download('contributions', 'contributions.csv', 'demo-contributions-file')}
            disabled={downloading !== null}
          >
            {downloading === 'contributions' ? (
              <>
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                Downloading…
              </>
            ) : (
              <>
                <Download className="mr-2 h-3.5 w-3.5" />
                Download contributions.csv
              </>
            )}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => download('expenses', 'expenses.csv', 'demo-expenses-file')}
            disabled={downloading !== null}
          >
            {downloading === 'expenses' ? (
              <>
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                Downloading…
              </>
            ) : (
              <>
                <Download className="mr-2 h-3.5 w-3.5" />
                Download expenses.csv
              </>
            )}
          </Button>
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
      </CardContent>
    </Card>
  );
}
